// bodyPerfection.js - the strict "is this governing body PERFECT?" test (Brian, 2026-10-19).
// Pure: no I/O. The SAME file is used by jarvis-watch (node) and the Command Center (browser).
//
// A body is PERFECT only if ALL hold:
//  C1 universe enumerated from the body's OWN published list  (else UNMEASURED, never perfect)
//  C2 zero missing championships against that list
//  C3 zero phantoms / wrong labels / wrong divisions
//  C4 every CONCLUDED championship crowned (individual titles: school + individual reference)
//  C5 every date real-sourced and carrying the year; no "+52wks" on a Confirmed record
//  C6 every record has governing_body, state (unless national) and an assigned rep
//  C7 no key collisions (and no null key)
// Unit of a body = the governing_body value, composite qualifier kept (registry rule).

export const CRITERIA = {
  C1: 'universe not enumerated from the body\'s own list',
  C2: 'missing championships',
  C3: 'phantoms / wrong labels or divisions',
  C4: 'concluded but not crowned',
  C5: 'date not real-sourced (projected, +52wks, dateless, no year)',
  C6: 'missing body / state / assigned rep',
  C7: 'key collision or null key',
}

const STATE_REQUIRED_LEVELS = /^(state|provincial|district|city|high school|class\b)/i
const STATE_REQUIRED_SEGMENTS = new Set(['HS State', 'Canada'])
const INDIVIDUAL = /\bindividual\b|\bsingles\b|\bdoubles\b|medalist|\b\d{2,3}\s?(lb|lbs)\b|weight class|all-around/i
const PROJECTED_SOURCE = /\+\s*\d+\s*wks?\b|to confirm|to ingest|confirm from/i
const REAL_STATUS = new Set(['Confirmed', 'Dated'])

const norm = (s) => String(s == null ? '' : s).trim()

export function recordDefects(r, keyCount, today) {
  const d = []
  const date = norm(r.sr_event_date)
  const src = norm(r.sr_date_source)
  const status = norm(r.sr_date_status)
  const hasYear = /^\d{4}-\d{2}-\d{2}/.test(date)
  // C5
  if (!hasYear) d.push(['C5', 'no dated year'])
  else if (status === 'Confirmed' && /\+\s*\d+\s*wks?/i.test(src)) d.push(['C5', 'Confirmed but +52wks'])
  else if (!REAL_STATUS.has(status)) d.push(['C5', 'projected date (status ' + (status || 'blank') + ')'])
  else if (!src) d.push(['C5', status + ' but no date source on the record'])
  else if (PROJECTED_SOURCE.test(src)) d.push(['C5', 'date source says projected / to confirm'])
  // C4 - concluded = a real, dated event before today
  if (hasYear && date.slice(0, 10) < today && REAL_STATUS.has(status)) {
    const indiv = INDIVIDUAL.test(norm(r.championship_name))
    if (!norm(r.current_champion)) d.push(['C4', 'concluded, nobody crowned'])
    else if (indiv && !norm(r.sr_winning_school)) d.push(['C4', 'individual title without the school'])
  }
  // C6
  if (!norm(r.governing_body)) d.push(['C6', 'no governing_body'])
  const needsState = STATE_REQUIRED_SEGMENTS.has(norm(r.sr_segment)) || STATE_REQUIRED_LEVELS.test(norm(r.sr_level))
  if (needsState && !norm(r.sr_state)) d.push(['C6', 'no state'])
  if (!norm(r.sr_assigned_rep)) d.push(['C6', 'no assigned rep'])
  // C7
  const k = norm(r.sr_championship_key)
  if (!k) d.push(['C7', 'null key'])
  else if ((keyCount.get(k) || 0) > 1) d.push(['C7', 'key collision'])
  return d
}

function ledgerIndex(ledger) {
  const byGb = new Map()
  const entries = []
  for (const b of ledger.bodies || []) { entries.push(b); for (const g of b.gb) byGb.set(g.toLowerCase(), b) }
  const bc = ledger.bowlCommittees
  if (bc) {
    const basis = 'bowl committee: single-title body (BOWL-COMMITTEE-BOOK-2026-10-17 / SCOPE-LEDGER-BOWLS-2026-10-18)'
    for (const n of bc.held || []) {
      const stale = bc.staleLabel && bc.staleLabel[n]
      const b = { key: 'bowl:' + n, name: n, gb: [n], national: true, official: 1, statedMissing: 0, statedPhantoms: stale ? 1 : 0, source: basis, note: stale || undefined, bowl: true }
      entries.push(b); byGb.set(n.toLowerCase(), b)
    }
    for (const n of bc.outOfScope || []) {
      const b = { key: 'bowl:' + n, name: n, gb: [n], national: true, official: 0, statedMissing: 0, statedPhantoms: 0, source: basis, note: 'game no longer exists; its record is still live (marked Out of Scope, not archived)', bowl: true }
      entries.push(b); byGb.set(n.toLowerCase(), b)
    }
    for (const n of bc.createdUnderBlankBody || []) {
      const b = { key: 'bowl:' + n, name: n, gb: [n], national: true, official: 1, statedMissing: 0, statedPhantoms: 0, source: basis, note: 'record exists but under a BLANK governing_body', bowl: true, blankBodyHolding: true, heldUnderBlank: 1 }
      entries.push(b); byGb.set(n.toLowerCase(), b)
    }
  }
  return { byGb, entries }
}

// Reuse the same bowl-committee and enumeration rules in the market census.
export const enumeratedBodies = (ledger) => ledgerIndex(ledger).entries

export function evaluate(records, ledger, today = new Date().toISOString().slice(0, 10)) {
  const keyCount = new Map()
  for (const r of records) { const k = norm(r.sr_championship_key); if (k) keyCount.set(k, (keyCount.get(k) || 0) + 1) }
  const { byGb, entries } = ledgerIndex(ledger)
  const bodies = new Map()
  let blankBody = 0
  for (const r of records) {
    const gb = norm(r.governing_body)
    if (!gb) { blankBody++; continue }
    const led = byGb.get(gb.toLowerCase())
    const id = led ? led.key : 'gb:' + gb
    if (!bodies.has(id)) bodies.set(id, { id, name: led ? led.name : gb, gbValues: new Set(), ledger: led || null, records: [], outOfScope: 0 })
    const b = bodies.get(id); b.gbValues.add(gb)
    if (norm(r.sr_scope_status) === 'Out of Scope') { b.outOfScope++; continue }
    b.records.push(r)
  }
  const ledgered = new Set([...bodies.values()].map((b) => b.ledger).filter(Boolean))
  for (const e of entries) if (!ledgered.has(e)) bodies.set(e.key, { id: e.key, name: e.name, gbValues: new Set(), ledger: e, records: [], outOfScope: 0 })

  const out = []
  for (const b of bodies.values()) {
    const fails = { C1: 0, C2: 0, C3: 0, C4: 0, C5: 0, C6: 0, C7: 0 }
    const detail = {}
    const add = (c, why, n = 1) => { fails[c] += n; detail[why] = (detail[why] || 0) + n }
    for (const r of b.records) for (const [c, why] of recordDefects(r, keyCount, today)) add(c, why)
    const held = b.records.length
    let missing = 0, phantoms = 0
    if (!b.ledger) fails.C1 = 1
    else {
      const L = b.ledger
      // records parked under a BLANK governing_body are not missing titles - they are C6; do not derive a count floor for them
      missing = L.heldUnderBlank ? (L.statedMissing || 0) : Math.max(L.statedMissing || 0, (L.official || 0) - held)
      phantoms = Math.max(L.statedPhantoms || 0, held - (L.official || 0)) + b.outOfScope
      if (missing) add('C2', 'missing championships', missing)
      if (phantoms) add('C3', b.outOfScope ? 'phantom record still live (Out of Scope, not archived)' : 'phantom / wrong-shape records', phantoms)
      if (L.heldUnderBlank) add('C6', 'records named for this body but filed under a BLANK governing_body (C2/C3 cannot be checked until they are attributed)', L.heldUnderBlank)
      else if (held === 0 && L.official > 0) add('C6', 'body holds 0 records under its own name', 1)
    }
    const failing = Object.keys(fails).filter((c) => fails[c] > 0)
    const status = !b.ledger ? 'UNMEASURED' : failing.length ? 'FAILING' : 'PERFECT'
    const recordFails = ['C4', 'C5', 'C6', 'C7'].filter((c) => fails[c] > 0)
    const top = status === 'FAILING' ? failing.slice().sort((x, y) => fails[y] - fails[x] || x.localeCompare(y))[0] : null
    out.push({
      id: b.id, name: b.name, gbValues: [...b.gbValues], status, held,
      official: b.ledger ? b.ledger.official : null, missing, phantoms,
      fails, failing, topCriterion: top, detail,
      enumerationSource: b.ledger ? b.ledger.source : null, note: b.ledger ? b.ledger.note || null : null,
      criteriaFailed: failing.length, defectTotal: Object.values(fails).reduce((s, n) => s + n, 0),
      recordHygieneClean: recordFails.length === 0 && held > 0,
    })
  }
  // distance-to-perfect: fewest criteria failed first, then the smallest amount of work. A missing source on an already-real date is one field;
  // a projected date needs research; a missing title needs a create; an unenumerated universe needs the whole body researched.
  const W = (why) => /no date source on the record/.test(why) ? 1 : /missing championships/.test(why) ? 3 : 2
  for (const x of out) x.distance = (x.status === 'UNMEASURED' ? 50 : 0) + Object.entries(x.detail).reduce((s, [why, n]) => s + W(why) * n, 0)
  out.sort((a, b) => a.criteriaFailed - b.criteriaFailed || a.distance - b.distance || a.name.localeCompare(b.name))
  const count = (s) => out.filter((x) => x.status === s).length
  const failingByTop = {}
  for (const x of out) if (x.status === 'FAILING') failingByTop[x.topCriterion] = (failingByTop[x.topCriterion] || 0) + 1
  const summary = {
    today, records: records.length, recordsWithNoBody: blankBody,
    totalBodies: out.length, perfect: count('PERFECT'), failing: count('FAILING'), unmeasured: count('UNMEASURED'),
    failingByTopCriterion: failingByTop,
    unmeasuredWithCleanRecords: out.filter((x) => x.status === 'UNMEASURED' && x.recordHygieneClean).length,
    reconciles: count('PERFECT') + count('FAILING') + count('UNMEASURED') === out.length,
  }
  return { summary, bodies: out }
}
